import { TextInput } from 'common-front/src/designSystem/components/textInput';
import { TextInput as TextInputForm } from 'common-front/src/designSystem/form/textInput';
import { ToggleText } from 'common-front/src/designSystem/form/toggle';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { I } from 'common/src/designSystem/components/i';
import { AccreditationsSlotId } from 'common/src/generated/types';
import { ICreateUpdateDelegationValues } from 'common/src/input/delegationInput';
import { isNonEmptyArray } from 'common/src/util/array';
import { preventDefault } from 'common/src/util/links';
import { produce } from 'immer';
import { difference } from 'lodash-es';
import * as React from 'react';

interface IDelegationAccreditationActionsProps {
    hasSlots: boolean;
    prefix: string;
    selectedSlotsIds: AccreditationsSlotId[];
    slotsIds: AccreditationsSlotId[];
    values: ICreateUpdateDelegationValues;

    change(name: string, value: any): void;
}

export const DelegationAccreditationActions = (props: IDelegationAccreditationActionsProps) => {
    const { translate } = useHeavent();
    const [total, _setTotal] = React.useState<number | undefined>(undefined);
    const setTotal = React.useCallback(
        (newTotal: number | undefined) => {
            props.change(
                'delegation.accreditationsSlots',
                produce(props.values.delegation.accreditationsSlots, (accreditationsSlots) => {
                    accreditationsSlots.forEach((as) => {
                        if (props.slotsIds.includes(as.accreditationSlotId)) {
                            as.maxResources = newTotal;
                        }
                    });
                })
            );
            _setTotal(newTotal);
        },
        [props.selectedSlotsIds, props.slotsIds, props.values, props.change, _setTotal]
    );
    const firstSelectedAccreditationSlot = React.useMemo(
        () =>
            props.values.delegation.accreditationsSlots.find(
                (as) => as.accreditationSlotId === props.selectedSlotsIds[0]
            ),
        [props.selectedSlotsIds, props.values]
    );
    const isFirstDefault = firstSelectedAccreditationSlot?.isDefault ?? false;
    const isFirstVisible = firstSelectedAccreditationSlot?.isVisible ?? true;
    const toggleAccredit = React.useCallback(() => {
        props.change(
            'delegation.accreditationsSlots',
            produce(props.values.delegation.accreditationsSlots, (accreditationsSlots) => {
                accreditationsSlots.forEach((as) => {
                    if (props.selectedSlotsIds.includes(as.accreditationSlotId)) {
                        if (isFirstDefault) {
                            as.isDefault = false;
                            as.isVisible = true;
                        } else {
                            as.isDefault = true;
                        }
                    }
                });
            })
        );
        props.change('selectedAccreditationsSlotsIds', []);
    }, [props.selectedSlotsIds, props.values, props.change, isFirstDefault]);
    const toggleVisibility = React.useCallback(() => {
        props.change(
            'delegation.accreditationsSlots',
            produce(props.values.delegation.accreditationsSlots, (accreditationsSlots) => {
                accreditationsSlots.forEach((as) => {
                    if (props.selectedSlotsIds.includes(as.accreditationSlotId)) {
                        if (isFirstVisible) {
                            as.isDefault = true;
                            as.isVisible = false;
                        } else {
                            as.isVisible = true;
                        }
                    }
                });
            })
        );
        props.change('selectedAccreditationsSlotsIds', []);
    }, [props.selectedSlotsIds, props.values, props.change, isFirstVisible]);
    const onDelete = React.useCallback(() => {
        props.change(
            'selectedAccreditationsSlotsIds',
            difference(props.values.selectedAccreditationsSlotsIds, props.selectedSlotsIds)
        );
        props.change(
            'delegation.accreditationsSlots',
            props.values.delegation.accreditationsSlots.filter(
                (as) => !props.selectedSlotsIds.includes(as.accreditationSlotId)
            )
        );
    }, [props.selectedSlotsIds, props.values, props.change]);

    if (isNonEmptyArray(props.selectedSlotsIds)) {
        return (
            <Flex gap="3" onClick={preventDefault}>
                <Flex
                    align="center"
                    css={{ color: '$gray800', cursor: 'pointer' }}
                    gap="2"
                    onClick={toggleAccredit}
                >
                    <Box fontSize="textXs">
                        <I icon="badge" />
                    </Box>

                    <Box fontWeight="medium">
                        {isFirstDefault
                            ? translate('ne_pas_accr_dit_59893')
                            : translate('accr_diter_par_72403')}
                    </Box>
                </Flex>

                <Flex
                    align="center"
                    css={{ color: '$gray800', cursor: 'pointer' }}
                    gap="2"
                    onClick={toggleVisibility}
                >
                    <Box fontSize="textXs">
                        <I icon={isFirstVisible ? 'eye-slash' : 'eye'} />
                    </Box>

                    <Box fontWeight="medium">
                        {isFirstVisible
                            ? translate('masquer_34645')
                            : translate('rendre_visible_37057')}
                    </Box>
                </Flex>

                <Flex align="center" css={{ color: '$gray800', cursor: 'pointer' }} gap="2">
                    <Box fontWeight="medium">{translate('total_07173')}</Box>

                    <Box width={100}>
                        <TextInput
                            min={0}
                            rightIcon="xmark"
                            shouldParseAsInt={true}
                            value={total ?? ''}
                            onChange={setTotal}
                            onRightIconClick={() => setTotal(undefined)}
                        />
                    </Box>
                </Flex>

                <Flex
                    align="center"
                    css={{ color: '$gray800', cursor: 'pointer' }}
                    gap="2"
                    onClick={onDelete}
                >
                    <Box fontSize="textXs">
                        <I icon="trash-can" />
                    </Box>

                    <Box fontWeight="medium">{translate('supprimer_43083')}</Box>
                </Flex>
            </Flex>
        );
    } else {
        return (
            <Flex align="center" gap="6" onClick={preventDefault}>
                {props.hasSlots && (
                    <Flex align="center" gap="2">
                        <Box>{translate('limite_totale_35352')}</Box>

                        <Box width={60}>
                            <TextInputForm name={`${props.prefix}.maxResources`} />
                        </Box>
                    </Flex>
                )}

                <ToggleText name={`${props.prefix}.willAutoAccredit`}>
                    {translate('attribuer_autom_13895')}
                </ToggleText>
            </Flex>
        );
    }
};
